/**
 * PayCashFileAttachment — renders a `<file>url;name;size;ext</file>` tag sent by
 * the PayCash app's own chat client.
 *
 * PayCash encrypts the file bytes with the exact same AES key and IV that
 * encrypt the enclosing message (reverse-engineered from the official PayCash
 * APK, 2026-09-06 — see docs/LEARNINGS.md and `chain-eos/mechat/crypto.ts`), so
 * we already have everything needed to decrypt it: `message.iv` and
 * `message.ephemPubKey`, both already present on the decrypted chain message.
 *
 * Same trust gate as NftAttachment: a file referenced by someone the user has
 * never written to is not fetched automatically — downloading would both hit
 * PayCash's server on a stranger's command and reveal the user's IP to it.
 */

import { useEffect, useState } from 'react'
import { decryptPayCashFile, proxyUrl, type EncryptedMessage } from '@properwallet/chain-eos'
import { useTranslation } from '../hooks/useTranslation'
import { useWalletStore } from '../store/wallet.store'

const IMAGE_EXTS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic'])

function guessMimeType(ext: string): string {
  const e = ext.trim().toLowerCase()
  if (e === 'jpg' || e === 'jpeg') return 'image/jpeg'
  if (e === 'png') return 'image/png'
  if (e === 'gif') return 'image/gif'
  if (e === 'webp') return 'image/webp'
  if (e === 'bmp') return 'image/bmp'
  if (e === 'pdf') return 'application/pdf'
  return 'application/octet-stream'
}

const DOC_EXTS = new Set(['doc', 'docx', 'odt', 'rtf', 'txt'])
const SHEET_EXTS = new Set(['xls', 'xlsx', 'ods', 'csv'])
const SLIDES_EXTS = new Set(['ppt', 'pptx', 'odp'])
const ARCHIVE_EXTS = new Set(['zip', 'rar', '7z', 'tar', 'gz', 'bz2'])
const AUDIO_EXTS = new Set(['mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a'])
const VIDEO_EXTS = new Set(['mp4', 'mov', 'avi', 'mkv', 'webm', '3gp'])

/** Non-image file icon, matched by extension — PDF/doc/sheet/etc. get a
 * recognizable glyph instead of one generic 📄 for everything. */
function guessFileIcon(ext: string): string {
  const e = ext.trim().toLowerCase()
  if (e === 'pdf') return '📕'
  if (DOC_EXTS.has(e)) return '📝'
  if (SHEET_EXTS.has(e)) return '📊'
  if (SLIDES_EXTS.has(e)) return '📑'
  if (ARCHIVE_EXTS.has(e)) return '🗜️'
  if (AUDIO_EXTS.has(e)) return '🎵'
  if (VIDEO_EXTS.has(e)) return '🎬'
  return '📄'
}

function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return ''
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

// files.paycash.online sends no CORS headers, so a browser (dev server or
// production web build) can't fetch it cross-origin directly — the request
// dies with "blocked by CORS policy" before decryption ever runs. Route it
// through the same-origin proxy instead (see vite.config.ts's dev middleware
// and api/paycash-file-proxy for the Vercel twin — same pattern already used
// for IPFS gateways). Native app builds could hit the URL directly, but
// routing everything through the proxy keeps behavior identical everywhere.
//
// "<account>/<name>" goes in a query param, not path segments: this
// project's Vercel routing 404s any API route matched by more than one
// dynamic path segment (confirmed on the pre-existing ipfs-proxy too, not
// just this new one), and PayCash's path is always account + filename.
const PAYCASH_FILES_PREFIX = 'https://files.paycash.online/api/v1/files/'

function toProxyUrl(url: string): string {
  if (url.startsWith(PAYCASH_FILES_PREFIX)) {
    const relPath = url.slice(PAYCASH_FILES_PREFIX.length)
    return proxyUrl(`/paycash-file-proxy?path=${encodeURIComponent(relPath)}`)
  }
  return url
}

// One decrypted object URL per (url, iv) pair for the app session — a
// conversation full of the same image costs one download+decrypt.
const cache = new Map<string, string | null>()
const inFlight = new Map<string, Promise<string | null>>()

async function resolveObjectUrl(
  url: string,
  fileExt: string,
  message: Pick<EncryptedMessage, 'iv' | 'ephemPubKey'>,
  privateKey: string,
): Promise<string | null> {
  const cacheKey = `${url}|${message.iv}`
  if (cache.has(cacheKey)) return cache.get(cacheKey) ?? null
  const pending = inFlight.get(cacheKey)
  if (pending) return pending

  const task = (async () => {
    const res = await fetch(toProxyUrl(url))
    if (!res.ok) throw new Error(`fetch ${res.status}`)
    const encrypted = new Uint8Array(await res.arrayBuffer())
    const plain = await decryptPayCashFile(encrypted, message, privateKey)
    // Copy into a concrete ArrayBuffer — Blob's typings reject the generic
    // ArrayBufferLike that a Uint8Array<ArrayBufferLike> carries under strict tsc.
    const buf = new ArrayBuffer(plain.byteLength)
    new Uint8Array(buf).set(plain)
    const blob = new Blob([buf], { type: guessMimeType(fileExt) })
    return URL.createObjectURL(blob)
  })()
    .then(objectUrl => {
      cache.set(cacheKey, objectUrl)
      return objectUrl
    })
    .catch(() => {
      cache.set(cacheKey, null)
      return null
    })
    .finally(() => inFlight.delete(cacheKey))

  inFlight.set(cacheKey, task)
  return task
}

export default function PayCashFileAttachment({
  url,
  fileName,
  fileSize,
  fileExt,
  message,
  trusted,
  ownMessage = false,
  localPreviewUrl,
  onReveal,
}: {
  url: string
  fileName: string
  fileSize: number
  fileExt: string
  message: Pick<EncryptedMessage, 'iv' | 'ephemPubKey'>
  /** Sender is someone the user has written to — safe to fetch without asking. */
  trusted: boolean
  /**
   * This is our OWN sent message. Our uploaded bytes are encrypted to the
   * recipient and the ephemeral key was discarded, so we can never fetch+decrypt
   * them back — show `localPreviewUrl` (kept only for this session) or a plain
   * "file sent" card, and never touch the network.
   */
  ownMessage?: boolean
  /** Session-only object URL of the file we just sent, for our own preview. */
  localPreviewUrl?: string
  /** Called when the user reveals an untrusted attachment, so the choice can persist. */
  onReveal?: () => void
}) {
  const { t } = useTranslation()
  const privateKey = useWalletStore(s => s.unlockedPrivateKey)
  const [revealed, setRevealed] = useState(trusted)
  const cacheKey = `${url}|${message.iv}`
  const [objectUrl, setObjectUrl] = useState<string | null | undefined>(cache.get(cacheKey))

  useEffect(() => {
    if (ownMessage || !revealed || objectUrl !== undefined || !privateKey) return
    let cancelled = false
    void resolveObjectUrl(url, fileExt, message, privateKey).then(u => {
      if (!cancelled) setObjectUrl(u)
    })
    return () => {
      cancelled = true
    }
  }, [ownMessage, revealed, objectUrl, url, fileExt, message, privateKey])

  // Our own sent file — never fetch (can't decrypt our own upload). Show the
  // in-session preview if we still have it, otherwise a simple "sent" card.
  if (ownMessage) {
    return (
      <div style={{ borderRadius: 10, overflow: 'hidden', background: 'rgba(0,0,0,0.35)' }}>
        {localPreviewUrl ? (
          <img
            src={localPreviewUrl}
            alt={fileName}
            loading="lazy"
            style={{ display: 'block', width: '100%', maxHeight: 260, objectFit: 'cover' }}
          />
        ) : (
          <div className="px-3 py-4 flex items-center gap-2">
            <span style={{ fontSize: 22 }} aria-hidden>
              {guessFileIcon(fileExt)}
            </span>
            <div className="min-w-0">
              <p className="text-xs text-white truncate">{fileName}</p>
              <p className="text-[10px]" style={{ color: '#6b7280' }}>
                {t('mechat.fileSent')}
              </p>
            </div>
          </div>
        )}
        <div className="px-3 py-2">
          <p className="text-[10px] truncate" style={{ color: '#6b7280' }}>
            {fileName}
            {fileSize ? ` · ${formatBytes(fileSize)}` : ''}
          </p>
        </div>
      </div>
    )
  }

  if (!revealed) {
    return (
      <button
        type="button"
        onClick={() => {
          onReveal?.()
          setRevealed(true)
        }}
        className="w-full text-left"
        style={{
          borderRadius: 10,
          border: '1px solid rgba(240,163,94,0.55)',
          background: 'rgba(240,163,94,0.12)',
          padding: '12px 14px',
          cursor: 'pointer',
        }}
      >
        <p className="text-xs flex items-center gap-1.5" style={{ color: '#f0a35e', fontWeight: 600 }}>
          <span aria-hidden>⚠️</span>
          {t('mechat.fileHidden')}
        </p>
        <p className="text-[11px] mt-1" style={{ color: '#f6c390' }}>
          {t('mechat.fileReveal')} · {fileName}
        </p>
      </button>
    )
  }

  if (objectUrl === undefined) {
    return (
      <div style={{ borderRadius: 10, background: 'rgba(255,255,255,0.04)', padding: '12px 14px' }}>
        <p className="text-xs" style={{ color: '#6b7280' }}>
          {t('mechat.fileDecrypting')}
        </p>
      </div>
    )
  }

  if (objectUrl === null) {
    return (
      <div style={{ borderRadius: 10, border: '1px dashed rgba(255,255,255,0.14)', padding: '10px 12px' }}>
        <p className="text-xs" style={{ color: '#9ca3af' }}>
          {t('mechat.fileError')} · {fileName}
        </p>
      </div>
    )
  }

  const isImage = IMAGE_EXTS.has(fileExt.trim().toLowerCase())

  return (
    <div style={{ borderRadius: 10, overflow: 'hidden', background: 'rgba(0,0,0,0.35)' }}>
      {isImage ? (
        <img
          src={objectUrl}
          alt={fileName}
          loading="lazy"
          style={{ display: 'block', width: '100%', maxHeight: 260, objectFit: 'cover' }}
        />
      ) : (
        <div className="px-3 py-4 flex items-center gap-2">
          <span style={{ fontSize: 22 }} aria-hidden>
            {guessFileIcon(fileExt)}
          </span>
          <div className="min-w-0">
            <p className="text-xs text-white truncate">{fileName}</p>
            <p className="text-[10px]" style={{ color: '#6b7280' }}>
              {t('mechat.fileFromPayCash')}
            </p>
          </div>
        </div>
      )}
      <div className="px-3 py-2 flex items-center justify-between gap-2">
        <p className="text-[10px] truncate" style={{ color: '#6b7280' }}>
          {fileName}
          {fileSize ? ` · ${formatBytes(fileSize)}` : ''}
        </p>
        <a
          href={objectUrl}
          download={fileName}
          className="text-[10px]"
          style={{ color: '#5cbf63', flexShrink: 0 }}
        >
          {isImage ? t('mechat.fileOpen') : t('mechat.fileDownload')}
        </a>
      </div>
    </div>
  )
}
